import { useContext } from 'react';

import ModalContext from '../../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import AppModal from '../../../../../../shared-components/AppModal';
import NotLicensedAppModal from '../../../../../../shared-components/NotLicensedAppModal';
import AppsContext from '../../../../_contexts/AppsContext';
import ModuleContext from '../../../../_contexts/ModuleContext';
import useMiniSiteMedialink from '../../../hooks/medialink/useMiniSiteMedialink';
import { getMedialinkById, getUrlForMiniSite, isMiniSite } from '../../../../../../api/medialink';
import { isDigibookApp, isOldDigibookApp } from '../../../../utils/digibookAppUtils';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../../constants/analyticsEvents';
import {
  App,
  isPInternMedialinkWithLinkedApp,
  isWebExternWithPlatformUrl,
  isWebInternWithPlatformUrl,
  LinkAppTypes,
  PlatformTypes,
} from '../../../../api/appsApi';

export default function useOtherApps() {
  const { apps } = useContext(AppsContext);
  const { module } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);
  const { openMiniSiteMedialink } = useMiniSiteMedialink();
  const { capture } = useAnalytics();
  const handleError = useThrowAsyncError();

  const otherApps = module
    ? apps.filter(
        app =>
          app.showAsButton &&
          !(app.platformType === PlatformTypes.P_INTERN && app.platformLinkType === LinkAppTypes.WORDLIST) &&
          !isDigibookApp(app) &&
          !isOldDigibookApp(app),
      )
    : [];

  const logAppAccessedEvent = async (app: App) => {
    if (!module) return;

    await capture({
      objectId: app.id,
      eventType: ANALYTICS_EVENT_TYPES.APP_ACCESSED,
      subLocationId: module.isExerciseModule
        ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
        : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
    });
  };

  const logMedialinkAccessedEvent = async (medialinkId: string) => {
    await capture({
      eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
      objectId: medialinkId,
      subLocationId: 'portaal-material-apps',
    });
  };

  const handleClick = async (app: App) => {
    if (!module) return;

    await logAppAccessedEvent(app);

    if (isWebInternWithPlatformUrl(app)) {
      return showModal(<AppModal id="app-modal" app={app} />);
    }

    if (isPInternMedialinkWithLinkedApp(app)) {
      try {
        const medialink = await getMedialinkById(app.moduleId, app.linkedApp.id);

        if (medialink && isMiniSite(medialink)) {
          if (medialink.miniSite.target === 'modal') {
            await logMedialinkAccessedEvent(medialink.id);

            const modal = await openMiniSiteMedialink(medialink, module);

            if (modal) {
              return showModal(modal);
            }
          }

          if (medialink.miniSite.target === 'same-window') {
            await logMedialinkAccessedEvent(medialink.id);

            const { url } = await getUrlForMiniSite(module.id, medialink.id);

            window.location.assign(url);

            return;
          }
        }
      } catch (error) {
        handleError(error);
      }
    }

    return showModal(<NotLicensedAppModal id="not-licensed-app-modal" appTypeId={app.appType.id} />);
  };

  const getAppActionProps = (app: App) => {
    if (isPInternMedialinkWithLinkedApp(app)) {
      const { miniSiteUrl } = app.linkedApp;

      if (miniSiteUrl) {
        return {
          as: 'link' as const,
          href: miniSiteUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick: () => {
            logAppAccessedEvent(app);
            logMedialinkAccessedEvent(app.linkedApp.id);
          },
        };
      }
    }

    if (isWebExternWithPlatformUrl(app)) {
      return {
        as: 'link' as const,
        href: app.platformUrl,
        target: '_blank',
        rel: 'noopener noreferrer',
        onClick: () => logAppAccessedEvent(app),
      };
    }

    return {
      as: 'button' as const,
      onClick: () => handleClick(app),
    };
  };

  return { otherApps, getAppActionProps };
}
